/**
 * GranulloGlassOverlay.jsx - Structural Transparency Layer
 * 
 * FOUNDER DIRECTIVE: Granullo Glass — Structural Overlay Mode
 * 
 * Reveals the structural relationships across the system:
 * - Dependency connections
 * - Exposure topology  
 * - Decision propagation paths
 * - Structural linkages between components
 * 
 * "A transparency layer for decision infrastructure"
 */

import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGranulloGlass, GLASS_CONFIG } from '../../contexts/GranulloGlassContext';

// Lens Highlight Pulse Component
const LensHighlightPulse = () => {
  const { lensHighlight } = useGranulloGlass();
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (lensHighlight.isActive && lensHighlight.element) {
      const rect = lensHighlight.element.getBoundingClientRect();
      setPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height
      });
    } else {
      setPosition(null);
    }
  }, [lensHighlight]);

  if (!position) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ 
        opacity: [0, 0.6, 0.3, 0],
        scale: [0.98, 1.02, 1.01, 1]
      }}
      transition={{ duration: 1.2, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        left: position.x - 4,
        top: position.y - 4,
        width: position.width + 8,
        height: position.height + 8,
        pointerEvents: 'none',
        zIndex: 9997,
        borderRadius: 8,
        border: '2px solid rgba(88, 166, 255, 0.8)',
        boxShadow: '0 0 20px rgba(88, 166, 255, 0.4), inset 0 0 15px rgba(88, 166, 255, 0.1)',
        background: 'rgba(88, 166, 255, 0.05)'
      }}
      data-testid="lens-highlight-pulse"
    />
  );
};

// Cascade Ripple Effect Component
const CascadeRipple = () => {
  const { cascadeState } = useGranulloGlass();

  if (!cascadeState.isActive) return null;

  return (
    <div 
      className="fixed inset-0 pointer-events-none z-[9990]"
      data-testid="cascade-ripple-overlay"
    >
      {/* Central ripple from source */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ 
          opacity: [0, 0.3, 0.1, 0],
          scale: [0.5, 2, 3, 4]
        }}
        transition={{ 
          duration: 1.5,
          ease: 'easeOut'
        }}
        className="absolute"
        style={{
          left: '50%',
          top: '50%',
          width: 200,
          height: 200,
          marginLeft: -100,
          marginTop: -100,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${GLASS_CONFIG.cascadeColor} 0%, transparent 70%)`,
        }}
      />

      {/* Progress indicator */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${cascadeState.progress}%` }}
        transition={{ duration: 0.1 }}
        className="absolute bottom-0 left-0 h-1"
        style={{
          background: `linear-gradient(90deg, ${GLASS_CONFIG.cascadeColor}, transparent)`,
        }}
      />

      {/* Cascade wave */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: cascadeState.progress > 20 ? 0.15 : 0 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at center, ${GLASS_CONFIG.nodeGlowColor} 0%, transparent 60%)`,
        }}
      />
    </div>
  );
};

// Structural Connection Lines (shown when Glass is active)
const StructuralConnections = () => {
  const { isGlassActive, config } = useGranulloGlass();
  const [connections, setConnections] = useState([]);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!isGlassActive) {
      setConnections([]);
      return;
    }

    // Find all elements with structural relationships
    const structuralElements = document.querySelectorAll('[data-structural-node]');
    const newConnections = [];

    structuralElements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const dependencies = element.getAttribute('data-dependencies')?.split(',') || [];
      
      dependencies.forEach(depId => {
        const depElement = document.querySelector(`[data-node-id="${depId}"]`);
        if (depElement) {
          const depRect = depElement.getBoundingClientRect();
          newConnections.push({
            id: `${index}-${depId}`,
            from: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
            to: { x: depRect.left + depRect.width / 2, y: depRect.top + depRect.height / 2 }
          });
        }
      });
    });

    setConnections(newConnections);
  }, [isGlassActive]);

  if (!isGlassActive || connections.length === 0) return null;

  return (
    <svg 
      className="fixed inset-0 pointer-events-none z-[9989]"
      style={{ width: '100vw', height: '100vh' }}
    >
      <defs>
        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={config.connectionColor} />
          <stop offset="50%" stopColor={config.cascadeColor} />
          <stop offset="100%" stopColor={config.connectionColor} />
        </linearGradient>
      </defs>
      
      {connections.map(conn => (
        <motion.line
          key={conn.id}
          x1={conn.from.x}
          y1={conn.from.y}
          x2={conn.to.x}
          y2={conn.to.y}
          stroke="url(#connectionGradient)"
          strokeWidth={1.5}
          strokeDasharray="4 4"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: config.connectionOpacity }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        />
      ))}
    </svg>
  );
};

// Glass Mode Indicator Badge
const GlassModeIndicator = () => {
  const { isGlassActive, toggleGlass } = useGranulloGlass();

  return (
    <AnimatePresence>
      {isGlassActive && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="fixed bottom-20 right-6 z-[9998]"
        >
          <button
            onClick={toggleGlass}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-medium tracking-wider"
            style={{
              background: 'rgba(22, 27, 34, 0.95)',
              border: '1px solid rgba(88, 166, 255, 0.3)',
              color: 'rgba(88, 166, 255, 0.9)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3), 0 0 20px rgba(88, 166, 255, 0.1)'
            }}
            data-testid="glass-mode-indicator"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-2 h-2 rounded-full"
              style={{ background: GLASS_CONFIG.cascadeColor }}
            />
            GLASS MODE
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Main Glass Overlay Component
export function GranulloGlassOverlay() {
  return (
    <>
      {/* Lens highlight pulse effect */}
      <LensHighlightPulse />
      
      {/* Cascade ripple animation */}
      <CascadeRipple />
      
      {/* Structural connection lines */}
      <StructuralConnections />
      
      {/* Glass mode indicator */}
      <GlassModeIndicator />
    </>
  );
}

export default GranulloGlassOverlay;
